import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useModal } from "@/contexts/ModalContext";
import Stack from "@/components/atoms/Stack";
import Text from "@/components/atoms/Text";

/**
 * Trade lock interception.
 *
 * `isCurrentCompanyLocked` on `useAuth()` is the single source for *whether* the company is locked; this hook
 * covers *what happens when the user tries to trade anyway*. It lives here rather than on `AuthContext`
 * because it needs `useModal()`, and `AuthProvider` is mounted outside `ModalProvider` in `src/index.tsx`.
 *
 * Everything below is read through refs so that `guardTradeLock` is stable for the lifetime of the component
 * *and* always sees the current lock state. Both matter: guarded handlers get cached by consumers — TanStack
 * Table holds on to the cell renderers built in a `useMemo` — so a guard that closed over
 * `isCurrentCompanyLocked` would keep answering with whatever the flag was when the columns were built. The
 * company list resolves after first paint, so that value is `false` on mount and the first click on a cached
 * handler would sail straight through to the wizard.
 */
export function useTradeLock() {
  const { isCurrentCompanyLocked } = useAuth();
  const { openModal } = useModal();
  const { t } = useTranslation();

  const isLockedRef = useRef(isCurrentCompanyLocked);
  const openModalRef = useRef(openModal);
  const tRef = useRef(t);

  useEffect(() => {
    isLockedRef.current = isCurrentCompanyLocked;
    openModalRef.current = openModal;
    tRef.current = t;
  }, [isCurrentCompanyLocked, openModal, t]);

  const showTradeLockModal = useCallback(() => {
    const translate = tRef.current;
    openModalRef.current({
      title: translate("company:tradeLock.modalTitle"),
      content: (
        <Stack gap="sm" data-testid="trade-lock-modal-content">
          <Text size="sm">{translate("company:tradeLock.modalBody")}</Text>
          <Text size="sm">
            <a href="mailto:info@reviver.no">info@reviver.no</a>
          </Text>
        </Stack>
      ),
      type: "informational",
      primaryAction: {
        text: translate("actions:close"),
        intent: "primary",
        id: "trade-lock-modal-close",
        // GlobalModal closes the modal itself once a primary action fires.
        onClick: () => {},
      },
    });
  }, []);

  /**
   * Replaces `action` with the explanation modal while the company is locked.
   *
   * `Args` is the wrapped action's parameter tuple, which is what keeps the guarded function's signature
   * identical to the one it wraps — `useOpenCreateOrder()` still takes `(contract, contractType)`, and a
   * wrong argument is still a compile error. The `extends unknown[]` is required rather than stylistic: a
   * type parameter can only sit in a rest position if it is constrained to an array type.
   */
  const guardTradeLock = useCallback(
    <Args extends unknown[]>(action: (...args: Args) => unknown) =>
      (...args: Args): void => {
        if (isLockedRef.current) {
          showTradeLockModal();
          return;
        }
        action(...args);
      },
    [showTradeLockModal]
  );

  return { guardTradeLock, showTradeLockModal };
}
